import React, { useState } from "react";
import { Trash2, Upload, FileText, Eye } from "react-feather";
import toast from "react-hot-toast";
import AttachmentPreviewModal from "../../../../components/common/AttachmentPreviewModal";
import {
  useUploadPettyCashRefillAttachmentMutation,
  useDeletePettyCashRefillAttachmentMutation,
  useLazyGetPettyCashRefillAttachmentContentQuery,
} from "../../pettyCashApi";

// One supporting document per refill request (Batch195, 2026-09-22) - the same
// upload / view (blob) / remove widget the voucher receipt uses (ReceiptUpload),
// on the refill endpoints. 2MB, JPEG/PNG/PDF, only while awaiting approval.
const RefillAttachment = ({ recordGuid, hasAttachment, fileName, canManage }) => {
  const [uploadAttachment, { isLoading: isUploading }] = useUploadPettyCashRefillAttachmentMutation();
  const [deleteAttachment] = useDeletePettyCashRefillAttachmentMutation();
  const [fetchContent] = useLazyGetPettyCashRefillAttachmentContentQuery();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isOpening, setIsOpening] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewBlob, setPreviewBlob] = useState(null);

  const handleUpload = async () => {
    if (!selectedFile) return toast.error("Choose a file first");
    if (selectedFile.size > 2 * 1024 * 1024) return toast.error("File must be 2MB or smaller");
    try {
      await uploadAttachment({ recordGuid, file: selectedFile }).unwrap();
      toast.success("Document attached");
      setSelectedFile(null);
    } catch (error) {
      toast.error(error?.message || "Could not attach the document");
    }
  };

  const handleView = async () => {
    setIsOpening(true);
    try {
      const blob = await fetchContent(recordGuid).unwrap();
      setPreviewBlob(blob);
      setShowPreview(true);
    } catch (error) {
      toast.error(error?.message || "Could not open the document");
    } finally {
      setIsOpening(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await deleteAttachment(recordGuid).unwrap();
      toast.success("Document removed");
    } catch (error) {
      toast.error(error?.message || "Could not remove the document");
    }
  };

  return (
    <div>
      <label className="form-label">Supporting document</label>
      {hasAttachment ? (
        <div className="d-flex align-items-center gap-2">
          <FileText size={16} className="text-muted" />
          <span>{fileName || "Document"}</span>
          <button type="button" className="btn btn-link p-1" onClick={handleView} disabled={isOpening} title="View">
            <Eye size={16} />
          </button>
          {canManage && (
            <button type="button" className="btn btn-link p-1 text-danger" onClick={handleDelete} title="Remove">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      ) : (
        canManage && (
          <div className="d-flex gap-2">
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              className="form-control"
              style={{ maxWidth: 280 }}
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
            <button type="button" className="btn btn-outline-primary" onClick={handleUpload} disabled={isUploading || !selectedFile}>
              <Upload size={14} className="me-1" /> {isUploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        )
      )}
      {!hasAttachment && !canManage && <div className="text-muted small">No document attached.</div>}

      <AttachmentPreviewModal
        show={showPreview}
        onClose={() => setShowPreview(false)}
        blob={previewBlob}
        fileName={fileName}
        title="Supporting document"
      />
    </div>
  );
};

export default RefillAttachment;
